import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { useHash } from '@confluence/route-manager/entry-points/RouteState.ts';

import type {
	ResolveElementByHash,
	ResolveFrameIndexByHash,
	TabParameters,
} from '../nativeTabsTypes.ts';
import { getTabTitleHash } from '../utils/tabTitleHash.ts';

type UseHashNavigationProps = {
	tabs: ReadonlyArray<TabParameters>;
	activeIndex?: number;
	onTabSelect: (index: number) => void;
	rootRef?: RefObject<HTMLElement>;
	resolveElementByHash?: ResolveElementByHash;
	resolveFrameIndexByHash?: ResolveFrameIndexByHash;
};

type ActivateHashTabOptions = {
	shouldScroll?: boolean;
};

// Holds a heading scroll until React/MBE commits the requested active frame.
type PendingFrameScroll = {
	element: HTMLElement;
	frameIndex: number;
};

const EXTENSION_FRAME_SELECTOR = '[data-extension-frame="true"]';

const decodeHashId = (rawHash: string): string | null => {
	if (!rawHash) {
		return null;
	}

	try {
		return decodeURIComponent(rawHash);
	} catch {
		return rawHash;
	}
};

const findElementById = (root: HTMLElement, id: string): HTMLElement | null => {
	if (typeof CSS !== 'undefined' && CSS.escape) {
		return root.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
	}

	return (
		Array.from(root.querySelectorAll<HTMLElement>('[id]')).find((element) => element.id === id) ??
		null
	);
};

const findAnchorByName = (root: HTMLElement, name: string): HTMLElement | null =>
	Array.from(root.querySelectorAll<HTMLAnchorElement>('a[name]')).find(
		(anchor) => anchor.getAttribute('name') === name,
	) ?? null;

const findElementByHash = (
	root: HTMLElement,
	hashId: string,
	resolveElementByHash?: ResolveElementByHash,
): HTMLElement | null => {
	const element =
		findElementById(root, hashId) ??
		findAnchorByName(root, hashId) ??
		resolveElementByHash?.(hashId) ??
		null;
	return element && root.contains(element) ? element : null;
};

const getExtensionFrameIndex = (element: HTMLElement): number => {
	const frame = element.closest<HTMLElement>(EXTENSION_FRAME_SELECTOR);
	const frameParent = frame?.parentElement;
	if (!frame || !frameParent) {
		return -1;
	}

	const frames = Array.from(
		frameParent.querySelectorAll<HTMLElement>(`:scope > ${EXTENSION_FRAME_SELECTOR}`),
	);
	return frames.indexOf(frame);
};

const scrollElementIntoViewAfterFrameActivation = (element: HTMLElement): void => {
	requestAnimationFrame(() => element.scrollIntoView());
};

export const useHashNavigation = ({
	tabs,
	activeIndex,
	onTabSelect,
	rootRef,
	resolveElementByHash,
	resolveFrameIndexByHash,
}: UseHashNavigationProps): void => {
	const routeHash = useHash();
	const activeIndexRef = useRef(activeIndex);
	const lastProcessedRouteHashRef = useRef<string | null>(null);
	const pendingFrameScrollRef = useRef<PendingFrameScroll | null>(null);

	useEffect(() => {
		activeIndexRef.current = activeIndex;
	}, [activeIndex]);

	useEffect(() => {
		const pendingFrameScroll = pendingFrameScrollRef.current;
		if (!pendingFrameScroll) {
			return;
		}

		if (pendingFrameScroll.frameIndex !== activeIndex) {
			pendingFrameScrollRef.current = null;
			return;
		}

		// The target frame is now active, so the heading is visible enough to scroll to.
		pendingFrameScrollRef.current = null;
		scrollElementIntoViewAfterFrameActivation(pendingFrameScroll.element);
	}, [activeIndex]);

	const scrollElementWhenFrameIsActive = useCallback((element: HTMLElement, frameIndex: number) => {
		if (activeIndexRef.current === frameIndex) {
			scrollElementIntoViewAfterFrameActivation(element);
			return;
		}

		pendingFrameScrollRef.current = { element, frameIndex };
	}, []);

	const activateHashTab = useCallback(
		(hashId: string | null, { shouldScroll = true }: ActivateHashTabOptions = {}) => {
			if (!hashId) {
				return false;
			}

			const tabIndex = tabs.findIndex((tab) => getTabTitleHash(tab.title) === hashId);
			const root = rootRef?.current;
			if (tabIndex !== -1) {
				onTabSelect(tabIndex);
				if (shouldScroll) {
					if (root) {
						findElementById(root, tabs[tabIndex].id)?.scrollIntoView();
					}
				}
				return true;
			}

			if (root) {
				const targetElement = findElementByHash(root, hashId, resolveElementByHash);
				if (targetElement) {
					const frameIndex = getExtensionFrameIndex(targetElement);
					if (frameIndex === -1 || frameIndex >= tabs.length) {
						return false;
					}

					onTabSelect(frameIndex);
					if (shouldScroll) {
						scrollElementWhenFrameIsActive(targetElement, frameIndex);
					}
					return true;
				}
			}

			if (!resolveFrameIndexByHash) {
				return false;
			}

			const frameIndex = resolveFrameIndexByHash(hashId);
			if (frameIndex === -1 || frameIndex >= tabs.length) {
				return false;
			}
			onTabSelect(frameIndex);
			return true;
		},
		[
			onTabSelect,
			resolveElementByHash,
			resolveFrameIndexByHash,
			rootRef,
			scrollElementWhenFrameIsActive,
			tabs,
		],
	);
	useEffect(() => {
		if (lastProcessedRouteHashRef.current === routeHash) {
			return;
		}

		lastProcessedRouteHashRef.current = routeHash;

		const hashId = decodeHashId(routeHash);
		if (!hashId) {
			return;
		}

		activateHashTab(hashId);
	}, [activateHashTab, routeHash]);
};
